import { qk, useUsers } from '@restaurant/api-client/react';
import type { Role, UserDto } from '@restaurant/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { PageHeader, Select, Spinner, Switch } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toastError } from '@/lib/errors';

export function UsersPage() {
  const { data: users = [] } = useUsers();
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ user?: UserDto } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: qk.users });

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Users" description="Staff reply to chats and manage orders. Owners also see the dashboard, settings and users." actions={<Button onClick={() => setDialog({})}><Plus /> Add user</Button>} />
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        <Card className="mx-auto max-w-3xl divide-y">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{u.name} {u.id === me?.id ? <span className="text-muted-foreground">(you)</span> : null}</p>
                <p className="truncate text-xs text-muted-foreground">{u.email}</p>
              </div>
              <Badge variant={u.role === 'owner' ? 'default' : 'secondary'}>{u.role}</Badge>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={u.active} disabled={u.id === me?.id} onCheckedChange={(v) => void api.users.update(u.id, { active: v }).then(refresh).catch(toastError)} /> Active
              </label>
              <Button size="sm" variant="outline" onClick={() => setDialog({ user: u })}>Edit</Button>
            </div>
          ))}
        </Card>
      </div>
      {dialog ? <UserDialog user={dialog.user} self={dialog.user?.id === me?.id} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); void refresh(); }} /> : null}
    </div>
  );
}

function UserDialog({ user, self, onClose, onSaved }: { user?: UserDto; self: boolean; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: user?.name ?? '', email: user?.email ?? '', role: (user?.role ?? 'staff') as Role, password: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (user) await api.users.update(user.id, { name: f.name, ...(self ? {} : { role: f.role }), ...(f.password ? { password: f.password } : {}) });
      else await api.users.create(f);
      toast.success('User saved');
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={user ? `Edit ${user.name}` : 'Add user'}>
        <div className="grid gap-4">
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Email"><Input type="email" value={f.email} disabled={!!user} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Role"><Select value={f.role} onValueChange={(v) => setF({ ...f, role: v as Role })} options={[{ value: 'staff', label: 'Staff' }, { value: 'owner', label: 'Owner' }]} /></Field>
          <Field label={user ? 'New password (optional)' : 'Password'} hint="At least 8 characters. Changing it signs the user out everywhere."><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
          <Button onClick={() => void save()} disabled={busy || !f.name || !f.email || (!user && f.password.length < 8)}>{busy ? <Spinner /> : null} Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
